/**
 * The two ways Thunder draws a compliance score: as a ring, and as a pill.
 *
 * The band thresholds (>=90 pass, >=70 warn) come from the engine, not from
 * here — they are part of the scoring contract (DOMAIN-MODEL §7).
 *
 * WHY THERE ARE TWO SHAPES. A ring is a proportion mark: it is worth its space only where
 * the console's one ring fits (`Ring.tsx`, one size everywhere). `ScorePill` carries the score
 * everywhere else — a dense action row, a list, a header. Callers pick by the room they have,
 * not by taste.
 */
import type { ReactNode } from 'react';
import { cn } from '@/lib/utils';
import { Ring } from './Ring';

export type Band = 'pass' | 'warn' | 'fail';

const BAND_VAR: Record<Band, string> = {
  pass: '--success',
  warn: '--warn',
  fail: '--danger',
};

const BAND_LABEL: Record<Band, string> = {
  pass: 'Compliant',
  warn: 'Partially compliant',
  fail: 'Non-compliant',
};

/**
 * The short band word, deliberately the same vocabulary the header's `ComplianceButton`
 * popover uses. Two controls showing the same site's score must not name its band differently.
 */
const BAND_WORD: Record<Band, string> = {
  pass: 'compliant',
  warn: 'partial',
  fail: 'non-compliant',
};

export interface ScoreRingProps {
  score: number;
  band: Band;
  className?: string;
}

/**
 * The score as the console's one ring (`Ring.tsx`, 2026-10-20): the same size, stroke and clear
 * space as every other ring, the score, its "%" and the band word on their own lines. It drew its
 * own circle at a size each caller chose, which is how a 38 px ring once printed its digits on its
 * own arc and its "%" as a 4 px glyph (the reason `ScorePill` exists); one size ends that. A place
 * with no room for a ring takes `ScorePill`.
 *
 * The digits are --fg on --card: 19.90:1 light, 17.31:1 dark, measured where they sit.
 */
export function ScoreRing({ score, band, className }: ScoreRingProps): ReactNode {
  const clamped = Math.max(0, Math.min(100, score));
  return (
    <div className={cn('shrink-0', className)} title={`${clamped}% — ${BAND_LABEL[band]}`}>
      <Ring
        fraction={clamped / 100}
        tone={band}
        value={String(clamped)}
        unit="%"
        note={BAND_WORD[band]}
        label={`Compliance score ${clamped} percent — ${BAND_LABEL[band]}`}
      />
    </div>
  );
}

export interface ScorePillProps {
  score: number;
  band: Band;
  /** Replaces the default hover text. Pass the pass/warn/fail breakdown where you have it. */
  title?: string | undefined;
  className?: string | undefined;
  /**
   * Names what the number is — "Compliance 83%" — in place of the band word. A bare
   * "83% partial" beside a site name was read as data coverage, not a compliance score
   * (Hamzah, 2026-09-29); the band stays on the mark and in the hover text.
   */
  prefix?: string | undefined;
}

/**
 * The score at text scale: `● 89% partial`.
 *
 * USED TO BE THE HEADER'S OWN SHAPE — `ComplianceBadge` drew exactly this pill inline, so a
 * reader who had learned it in the app bar would not have to learn a second thing on the
 * site page. The header now shows an icon with a dot instead (`ComplianceButton`, to reclaim
 * the width the pill spent), and this same `ScorePill` is what its popover opens into — so
 * the shape did not disappear, it moved one click deeper, and the vocabulary is still shared
 * rather than reinvented per screen. The band is carried by the dot, the border and the
 * tint, and named in words — status is never conveyed by colour alone (DESIGN-SYSTEM §1).
 *
 * WHY THE NUMBER IS NOT BAND-COLOURED. This and `StatusBadge` both used to paint their text
 * in the band colour over an 11-12% tint of that same colour. Measured, that is 4.16:1 for
 * amber (#b45309 on the composite rgb(242,232,224)) in light theme — under the 4.5:1 floor,
 * and the score is the one number on this page a reader must not squint at. So the digits
 * are --fg and the colour lives in the chrome. Measured on the PILL's own tinted composite,
 * which is where these digits sit: 16.44:1 light and 16.02:1 dark. (Not the 19.06/16.03 a
 * previous version of this comment claimed — that pair mixed a --bg measurement with a
 * composite one and contradicted the ring's own note above.)
 * Do not "restore consistency" by tinting the digits; it costs a third of the contrast.
 */
export function ScorePill({
  score,
  band,
  title,
  className,
  prefix,
}: ScorePillProps): ReactNode {
  const clamped = Math.max(0, Math.min(100, score));
  const colour = `var(${BAND_VAR[band]})`;

  return (
    <span
      className={cn(
        'inline-flex h-[32px] shrink-0 items-center gap-[7px] whitespace-nowrap',
        'rounded-[var(--radius-pill)] px-[11px]',
        className,
      )}
      style={{
        border: `1px solid color-mix(in srgb, ${colour} 40%, transparent)`,
        background: `color-mix(in srgb, ${colour} 11%, transparent)`,
      }}
      role="img"
      aria-label={`Compliance score ${clamped} percent — ${BAND_LABEL[band]}`}
      title={title ?? `${clamped}% — ${BAND_LABEL[band]}`}
    >
      <span
        className="size-[7px] shrink-0 rounded-full"
        style={{ background: colour }}
        aria-hidden
      />
      {/* The "%" is part of the number, not a separate line: a bare "89" next to a site
          name reads as a count of something. */}
      {prefix && (
        <span className="text-[12px] font-medium" style={{ color: 'var(--muted-fg)' }}>
          {prefix}
        </span>
      )}
      <span className="lm-num text-[14px] font-bold" style={{ color: 'var(--fg)' }}>
        {clamped}%
      </span>
      {!prefix && (
        <span className="text-[11.5px] font-medium" style={{ color: 'var(--muted-fg)' }}>
          {BAND_WORD[band]}
        </span>
      )}
    </span>
  );
}
